/**
 * useInvoiceOutreach.ts — Billing Phase 2
 * Hooks de outreach para invoices. Espejo exacto de useEstimateOutreach.ts,
 * más compose-reminder (recordatorio de pago) y mark-paid (acción manual).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

// ── Tipos ─────────────────────────────────────────────────────────────────────

export interface InvoiceOutreachDraft {
  id: string;
  invoiceId: number;
  contactId: number;
  sequenceOrder: number;
  draftType: 'invoice_review' | 'payment_reminder' | 'reply';
  subject: string;
  bodyHtml: string;
  bodyText: string;
  status: 'pending_review' | 'sent' | 'cancelled' | 'failed';
  cancellationReason: string | null;
  sendError: string | null;
  sendAttemptedAt: number | null;
  inReplyToMessageId: string | null;
  sentAt: number | null;
  sentMessageId: string | null;
  createdAt: number;
  updatedAt: number;
  generatedByClaude: number;
  claudeCostUsd: number | null;
}

export interface InvoiceOutreachReply {
  id: string;
  invoiceId: number;
  contactId: number;
  fromAddress: string;
  subject: string | null;
  bodyHtml: string | null;
  bodyText: string | null;
  receivedAt: number;
  messageId: string;
  inReplyTo: string | null;
  matchedDraftId: string | null;
  createdAt: number;
}

// ── Queries ───────────────────────────────────────────────────────────────────

export function useInvoiceDrafts(invoiceId: number | null) {
  return useQuery<{ drafts: InvoiceOutreachDraft[] }>({
    queryKey: ['invoice-outreach-drafts', invoiceId],
    queryFn: () => api.get<{ drafts: InvoiceOutreachDraft[] }>(`/api/billing/invoices/${invoiceId}/outreach/drafts`),
    enabled: invoiceId !== null,
  });
}

export function useInvoiceReplies(invoiceId: number | null) {
  return useQuery<{ replies: InvoiceOutreachReply[] }>({
    queryKey: ['invoice-outreach-replies', invoiceId],
    queryFn: () => api.get<{ replies: InvoiceOutreachReply[] }>(`/api/billing/invoices/${invoiceId}/outreach/replies`),
    enabled: invoiceId !== null,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useComposeInvoiceSend(invoiceId: number) {
  const qc = useQueryClient();
  return useMutation<{ draftId: string; existing?: boolean; subject?: string; costUsd?: number }, Error>({
    mutationFn: () =>
      api.post<{ draftId: string; existing?: boolean; subject?: string; costUsd?: number }>(
        `/api/billing/invoices/${invoiceId}/outreach/compose-send`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoice-outreach-drafts', invoiceId] });
    },
  });
}

// Recordatorio de pago — draft para revisión, nunca auto-enviado.
export function useComposeInvoiceReminder(invoiceId: number) {
  const qc = useQueryClient();
  return useMutation<{ draftId: string; existing?: boolean; subject?: string; costUsd?: number }, Error>({
    mutationFn: () =>
      api.post<{ draftId: string; existing?: boolean; subject?: string; costUsd?: number }>(
        `/api/billing/invoices/${invoiceId}/outreach/compose-reminder`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoice-outreach-drafts', invoiceId] });
    },
  });
}

export function useComposeInvoiceReply(invoiceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ replyId, bodyHtml, bodyText }: { replyId: string; bodyHtml: string; bodyText: string }) =>
      api.post<{ draftId: string; subject: string; bodyHtml: string; bodyText: string }>(
        `/api/billing/invoices/${invoiceId}/outreach/replies/${replyId}/compose-reply`,
        { bodyHtml, bodyText },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoice-outreach-drafts', invoiceId] });
    },
  });
}

export function useUpdateInvoiceDraft(invoiceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, subject, bodyHtml, bodyText }: { draftId: string; subject?: string; bodyHtml?: string; bodyText?: string }) =>
      api.patch<InvoiceOutreachDraft>(
        `/api/billing/invoices/${invoiceId}/outreach/drafts/${draftId}`,
        { subject, bodyHtml, bodyText },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoice-outreach-drafts', invoiceId] });
    },
  });
}

export function useSendInvoiceDraft(invoiceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draftId: string) =>
      api.post<{ ok: boolean; messageId: string }>(`/api/billing/invoices/${invoiceId}/outreach/drafts/${draftId}/send`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoice-outreach-drafts', invoiceId] });
      // Sending flips invoice.status server-side (draft→sent) — refresh the invoice too.
      void qc.invalidateQueries({ queryKey: ['billing-invoice', invoiceId] });
      void qc.invalidateQueries({ queryKey: ['billing-invoices'] });
    },
  });
}

export function useCancelInvoiceDraft(invoiceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, reason }: { draftId: string; reason?: string }) =>
      api.post<{ ok: boolean }>(`/api/billing/invoices/${invoiceId}/outreach/drafts/${draftId}/cancel`, reason ? { reason } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoice-outreach-drafts', invoiceId] });
    },
  });
}

// ── Mark paid (Billing Phase 2 — acción manual, sin gateway) ──────────────────

export function useMarkInvoicePaid(invoiceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<unknown>(`/api/billing/invoices/${invoiceId}/mark-paid`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-invoice', invoiceId] });
      void qc.invalidateQueries({ queryKey: ['billing-invoices'] });
    },
  });
}
